<script>
  import { onDestroy } from 'svelte'
  import {
    outcomeLabelRu,
    outcomeKind,
    fmtBetPnl,
    fmtDate,
    fmtNum,
    windowEndMs,
    fmtCloseCountdown,
  } from '../lib/format.js'

  /** Build stamp — verify in deployed SPA bundle. */
  const FEED_BUILD = '20260801-noExpand'

  /** Bets only — never mix SKIP rows here (SKIP lives in skips_collapsed). */
  export let items = []
  /** Show 15m/5m pill when feed is merged. */
  export let showHorizon = false
  /** Show BTC/ETH/… pill when multi-coin feed is merged. */
  export let showAsset = false

  /** Client clock for PENDING close countdown (1s tick). Pure frontend — no API. */
  let nowMs = Date.now()
  /** @type {ReturnType<typeof setInterval> | null} */
  let tick = null

  // Touch stamp so Vite keeps it in the bundle.
  void FEED_BUILD

  function isPending(bet) {
    const o = String(bet?.outcome || 'PENDING').toUpperCase()
    return o === 'PENDING' || o === ''
  }

  function stopTick() {
    if (tick != null) {
      clearInterval(tick)
      tick = null
    }
  }

  function startTick() {
    if (tick != null) return
    nowMs = Date.now()
    tick = setInterval(() => {
      nowMs = Date.now()
    }, 1000)
  }

  /** Tick only while at least one PENDING row needs countdown; clear when all settle. */
  $: {
    const list = Array.isArray(items) ? items : []
    if (list.some(isPending)) startTick()
    else stopTick()
  }

  onDestroy(stopTick)

  /**
   * Stable row identity across polls.
   * Prefer slug|horizon; fall back to ids that do not churn every refresh.
   */
  function rowKey(bet, index = 0) {
    const slug = String(bet?.slug || '').trim()
    const hz = String(bet?.horizon || '').trim().toLowerCase()
    const asset = String(bet?.asset || '').trim().toLowerCase()
    if (slug) return `${asset || 'na'}|${slug}|${hz || 'na'}`
    const id =
      bet?.bet_id ||
      bet?.id ||
      bet?.attempt_id ||
      bet?.order_id ||
      bet?.condition_id ||
      ''
    if (id) return `id:${String(id)}|${asset || 'na'}|${hz || 'na'}`
    const market = String(bet?.market || '').trim()
    const side = String(bet?.rule_signal || bet?.side || '').trim().toUpperCase()
    const ts = String(bet?.outcome_ts || bet?.timestamp || '').trim()
    if (market || ts) return `fb:${asset}|${market}|${side}|${ts}|${hz || 'na'}`
    return `idx:${index}|${asset || 'na'}|${hz || 'na'}`
  }

  function sideOf(bet) {
    const s = String(bet?.rule_signal || '').toUpperCase()
    if (s === 'UP') return 'Вверх'
    if (s === 'DOWN') return 'Вниз'
    return s || '—'
  }

  function voidTitle(outcome) {
    return String(outcome || '').toUpperCase() === 'VOID'
      ? 'Ставка отменена, средства возвращены на баланс'
      : undefined
  }

  /**
   * Pass ``clockMs`` from the template so Svelte invalidates every tick.
   * (Reading ``nowMs`` only inside this fn does NOT re-render the row.)
   * @param {any} bet
   * @param {number} clockMs
   */
  function closeLabel(bet, clockMs) {
    if (!isPending(bet)) return null
    return fmtCloseCountdown(windowEndMs(bet), clockMs)
  }
</script>

{#if !items || !items.length}
  <p class="pm-empty">Нет ставок за этот день.</p>
{:else}
  <ul class="pm-feed-list" data-feed-build={FEED_BUILD}>
    {#each items as bet, i (rowKey(bet, i))}
      <li class="pm-feed-item" data-bet-key={rowKey(bet, i)}>
        <div class="pm-feed-row">
          <div>
            <div>{fmtDate(bet.outcome_ts || bet.timestamp)}</div>
            <span
              class="pm-pill pm-pill--{outcomeKind(bet.outcome)}"
              title={voidTitle(bet.outcome)}
            >{outcomeLabelRu(bet.outcome)}</span>
            {#if showAsset && (bet.label || bet.asset)}
              <span class="pm-hz-pill pm-asset-pill"
                >{bet.label || String(bet.asset).toUpperCase()}</span
              >
            {/if}
            {#if showHorizon && bet.horizon}
              <span class="pm-hz-pill">{bet.horizon}</span>
            {/if}
            {#if closeLabel(bet, nowMs)}
              <div class="pm-meta mono pm-close-cd">{closeLabel(bet, nowMs)}</div>
            {/if}
          </div>
          <div>
            <strong>{bet.market || bet.slug || '—'}</strong>
            <div class="pm-meta">{sideOf(bet)}</div>
            {#if bet.size_notional != null}
              <div class="pm-meta mono">ставка {fmtNum(bet.size_notional)}</div>
            {/if}
          </div>
          <div class="pm-feed-row__end mono">
            <div>{fmtBetPnl(bet)}</div>
          </div>
        </div>
      </li>
    {/each}
  </ul>
{/if}

<style>
  .mono {
    font-variant-numeric: tabular-nums;
  }
  .pm-hz-pill {
    display: inline-block;
    margin-left: 0.35rem;
    padding: 0.05rem 0.4rem;
    border-radius: 999px;
    border: 1px solid var(--pm-line);
    font-family: var(--pm-mono);
    font-size: 0.62rem;
    font-weight: 500;
    color: var(--pm-ink-soft);
    background: var(--pm-paper-alt);
    vertical-align: middle;
  }
  .pm-close-cd {
    margin-top: 0.2rem;
    font-size: 0.72rem;
    color: var(--pm-ink-soft);
  }
  .pm-feed-item {
    display: block;
    padding: 0;
    border-bottom: 1px solid var(--pm-line);
    font-size: 0.82rem;
  }
  .pm-feed-item:last-child {
    border-bottom: 0;
  }
  .pm-feed-row {
    display: grid;
    grid-template-columns: 7.25rem 1fr auto;
    gap: 0.65rem;
    align-items: start;
    width: 100%;
    margin: 0;
    padding: 0.65rem 0;
    color: inherit;
    text-align: left;
    font: inherit;
  }
  .pm-feed-row__end {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  @media (max-width: 640px) {
    .pm-feed-row {
      grid-template-columns: 1fr;
      gap: 0.3rem;
    }
  }
</style>
